import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { SharedDraftLeaseCard } from '@/components/SharedPricingReview/SharedDraftLeaseCard';
import { getLongNumberStr } from '@/helpers/common';
import { Concession } from '@/types/lease';
import dayjs from 'dayjs';

// Types matching the public API response
interface PublicCurrentLeaseView {
  monthlyRent: number;
  leaseStartDate: string;
  leaseEndDate: string;
  totalConcessionAmount: number;
}

interface PublicLeaseView {
  id: string;
  unitId: string;
  monthlyRent: number;
  leaseStartDate: string;
  leaseEndDate: string;
  totalConcessionAmount: number;
  concessions: Concession[];
  externalApproval?: {
    approved: boolean;
    approvedAt?: string;
    approvedBy?: string;
  };
}

interface PublicUnitView {
  id: string;
  name: string;
  numBedrooms: number;
  sqft: number;
  floor?: number;
  currentLease?: PublicCurrentLeaseView;
}

interface PublicBuildingView {
  name: string;
  address?: string;
}

interface PublicShareResponse {
  share: {
    id: string;
    title?: string;
    description?: string;
    createdByEmail: string;
    expiresAt?: string;
  };
  leases: PublicLeaseView[];
  units: PublicUnitView[];
  building: PublicBuildingView;
}

// Get the API base URL from environment
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
    return 'http://127.0.0.1:5001/urbanmix-dev/us-central1/api';
  }
  // Use the VITE_API_SERVER environment variable for production
  const apiServer = import.meta.env.VITE_API_SERVER;
  if (apiServer) {
    return `${apiServer}/api`;
  }
  return '/api';
};

export default function SharedPricingReviewPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PublicShareResponse | null>(null);
  const [selectedLeaseIds, setSelectedLeaseIds] = useState<Set<string>>(new Set());
  const [approving, setApproving] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [showDifferenceAsPercent, setShowDifferenceAsPercent] = useState(true);

  // Fetch share data
  useEffect(() => {
    const fetchShareData = async () => {
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${getApiBaseUrl()}/shares/public/pricing-review/${shareId}`
        );

        if (response.status === 404) {
          setError('This share link is invalid or has been revoked');
          setLoading(false);
          return;
        }

        if (response.status === 410) {
          setError('This share link has expired');
          setLoading(false);
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || 'Failed to load share data');
          setLoading(false);
          return;
        }

        const shareData = await response.json();
        setData(shareData);
      } catch (err) {
        console.error('Error fetching share data:', err);
        setError('Failed to load share data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchShareData();
  }, [shareId]);

  // Create unit map for quick lookup
  const unitMap = useMemo(() => {
    if (!data) return new Map<string, PublicUnitView>();
    const map = new Map<string, PublicUnitView>();
    data.units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [data]);

  // Filter to unapproved leases
  const unapprovedLeases = useMemo(() => {
    if (!data) return [];
    return data.leases.filter((lease) => !lease.externalApproval?.approved);
  }, [data]);

  const approvedLeases = useMemo(() => {
    if (!data) return [];
    return data.leases.filter((lease) => lease.externalApproval?.approved);
  }, [data]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!data || data.leases.length === 0) return null;

    let totalCurrentRent = 0;
    let totalProposedRent = 0;
    let totalConcessions = 0;
    let validCount = 0;

    data.leases.forEach((lease) => {
      const unit = unitMap.get(lease.unitId);
      if (unit && unit.currentLease) {
        totalCurrentRent += unit.currentLease.monthlyRent;
        totalProposedRent += lease.monthlyRent;
        totalConcessions += lease.totalConcessionAmount || 0;
        validCount++;
      }
    });

    const avgCurrentRent = validCount > 0 ? totalCurrentRent / validCount : 0;
    const avgProposedRent = validCount > 0 ? totalProposedRent / validCount : 0;
    const avgConcessions = validCount > 0 ? totalConcessions / validCount : 0;
    const avgRentIncrease =
      avgCurrentRent > 0
        ? ((avgProposedRent - avgCurrentRent) / avgCurrentRent) * 100
        : 0;

    return {
      totalUnits: data.leases.length,
      avgCurrentRent,
      avgProposedRent,
      avgConcessions,
      avgRentIncrease,
      approvedCount: approvedLeases.length,
      pendingCount: unapprovedLeases.length,
    };
  }, [data, unitMap, approvedLeases.length, unapprovedLeases.length]);

  // Handle selection change
  const handleSelectionChange = (leaseId: string, selected: boolean) => {
    setSelectedLeaseIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(leaseId);
      } else {
        newSet.delete(leaseId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedLeaseIds.size === unapprovedLeases.length) {
      setSelectedLeaseIds(new Set());
    } else {
      setSelectedLeaseIds(new Set(unapprovedLeases.map((l) => l.id)));
    }
  };

  // Open approval dialog
  const handleOpenApprovalDialog = () => {
    if (selectedLeaseIds.size === 0) return;
    setApprovalDialogOpen(true);
  };

  // Handle approval submission
  const handleApprove = async () => {
    if (!reviewerName.trim()) {
      return;
    }

    setApproving(true);

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/shares/public/pricing-review/${shareId}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            leaseIds: Array.from(selectedLeaseIds),
            reviewerName: reviewerName.trim(),
            reviewerEmail: reviewerEmail.trim() || undefined,
            notes: approvalNotes.trim() || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to approve leases');
      }

      // Update local state to reflect approval
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          leases: prev.leases.map((lease) => {
            if (selectedLeaseIds.has(lease.id)) {
              return {
                ...lease,
                externalApproval: {
                  approved: true,
                  approvedAt: new Date().toISOString(),
                  approvedBy: reviewerEmail
                    ? `${reviewerName} (${reviewerEmail})`
                    : reviewerName,
                },
              };
            }
            return lease;
          }),
        };
      });

      setSelectedLeaseIds(new Set());
      setApprovalDialogOpen(false);
      setApprovalSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => setApprovalSuccess(false), 5000);
    } catch (err) {
      console.error('Error approving leases:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve leases');
    } finally {
      setApproving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <CircularProgress />
        <Typography className="ml-4">Loading pricing review...</Typography>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" color="error" gutterBottom>
            Unable to Load Review
          </Typography>
          <Typography color="textSecondary">{error}</Typography>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="shared-pricing-review-page px-8 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="h4" gutterBottom>
          {data.share.title || 'Pricing Review'}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {data.building.name}
          {data.building.address && ` - ${data.building.address}`}
        </Typography>
        {data.share.description && (
          <Typography variant="body2" color="textSecondary" className="mt-2">
            {data.share.description}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" display="block" className="mt-2">
          Shared by {data.share.createdByEmail}
          {data.share.expiresAt && (
            <> · Expires {dayjs(data.share.expiresAt).format('MMM D, YYYY')}</>
          )}
        </Typography>
      </div>

      {/* Success message */}
      {approvalSuccess && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          className="mb-4"
          onClose={() => setApprovalSuccess(false)}
        >
          Leases have been approved successfully. The property manager will be notified.
        </Alert>
      )}

      {/* Summary stats */}
      {summaryStats && (
        <Paper className="mb-6 p-4 bg-zinc-800/70" elevation={0}>
          <Typography variant="subtitle2" className="mb-3 font-bold">
            Summary Statistics
          </Typography>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <Typography variant="caption" color="textSecondary">
                Total Units
              </Typography>
              <Typography variant="h6">{summaryStats.totalUnits}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Current Rent
              </Typography>
              <Typography variant="h6">
                ${getLongNumberStr(Math.round(summaryStats.avgCurrentRent))}
              </Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Proposed Rent
              </Typography>
              <Typography variant="h6" color="primary">
                ${getLongNumberStr(Math.round(summaryStats.avgProposedRent))}
              </Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Rent Increase
              </Typography>
              <Typography
                variant="h6"
                color={
                  summaryStats.avgRentIncrease >= 0 ? 'success.main' : 'error.main'
                }
              >
                {summaryStats.avgRentIncrease >= 0 ? '+' : ''}
                {summaryStats.avgRentIncrease.toFixed(1)}%
              </Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Pending Approval
              </Typography>
              <Typography variant="h6">{summaryStats.pendingCount}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Approved
              </Typography>
              <Typography variant="h6" color="success.main">
                {summaryStats.approvedCount}
              </Typography>
            </div>
          </div>
        </Paper>
      )}

      {/* Display options */}
      {data.leases.length > 0 && (
        <div className="flex items-center justify-end mb-4">
          <ToggleButtonGroup
            value={showDifferenceAsPercent ? 'percent' : 'dollar'}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                setShowDifferenceAsPercent(value === 'percent');
              }
            }}
            size="small"
          >
            <ToggleButton value="percent">Change (%)</ToggleButton>
            <ToggleButton value="dollar">Change ($)</ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      {/* Approval actions */}
      {unapprovedLeases.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-zinc-800/30 rounded">
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedLeaseIds.size === unapprovedLeases.length}
                indeterminate={
                  selectedLeaseIds.size > 0 &&
                  selectedLeaseIds.size < unapprovedLeases.length
                }
                onChange={handleSelectAll}
              />
            }
            label={
              <Typography variant="body2">
                Select All ({selectedLeaseIds.size} of {unapprovedLeases.length} selected)
              </Typography>
            }
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={selectedLeaseIds.size === 0}
            onClick={handleOpenApprovalDialog}
          >
            Approve Selected ({selectedLeaseIds.size})
          </Button>
        </div>
      )}

      {/* Lease cards - Pending approval first */}
      <div className="space-y-4">
        {unapprovedLeases.length > 0 && (
          <>
            <Typography variant="subtitle2" color="textSecondary" className="mb-2">
              Pending Approval
            </Typography>
            {unapprovedLeases.map((lease) => {
              const unit = unitMap.get(lease.unitId);
              if (!unit) return null;

              return (
                <SharedDraftLeaseCard
                  key={lease.id}
                  draftLease={lease}
                  unit={unit}
                  isSelected={selectedLeaseIds.has(lease.id)}
                  onSelectionChange={handleSelectionChange}
                  showDifferenceAsPercent={showDifferenceAsPercent}
                />
              );
            })}
          </>
        )}

        {approvedLeases.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              className="mb-2 mt-6"
            >
              Already Approved
            </Typography>
            {approvedLeases.map((lease) => {
              const unit = unitMap.get(lease.unitId);
              if (!unit) return null;

              return (
                <SharedDraftLeaseCard
                  key={lease.id}
                  draftLease={lease}
                  unit={unit}
                  isSelected={false}
                  onSelectionChange={() => {}}
                  showDifferenceAsPercent={showDifferenceAsPercent}
                />
              );
            })}
          </>
        )}
      </div>

      {/* All approved message */}
      {unapprovedLeases.length === 0 && approvedLeases.length > 0 && (
        <Alert severity="success" className="mt-4">
          All leases in this review have been approved.
        </Alert>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" className="mb-4">
            You are about to approve {selectedLeaseIds.size} lease
            {selectedLeaseIds.size !== 1 ? 's' : ''}. Please provide your
            information for the approval record.
          </Typography>

          <TextField
            label="Your Name"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            fullWidth
            required
            className="mb-4"
            error={!reviewerName.trim()}
            helperText={!reviewerName.trim() ? 'Name is required' : ''}
          />

          <TextField
            label="Your Email (optional)"
            type="email"
            value={reviewerEmail}
            onChange={(e) => setReviewerEmail(e.target.value)}
            fullWidth
            className="mb-4"
          />

          <TextField
            label="Notes (optional)"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)} disabled={approving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={!reviewerName.trim() || approving}
            startIcon={approving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {approving ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
